import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

export interface ActionButton {
  icon: string;
  tooltip?: string;
  color?: 'primary' | 'accent' | 'warn' | 'default';
  onClick: (data: any, params: CellRendererParams<any>) => void;
  disabled?: (data: any) => boolean;
  hidden?: (data: any) => boolean;
}

/**
 * Action Buttons Cell Renderer
 *
 * Renders action buttons (edit, delete, view, etc.) in a cell.
 *
 * @example
 * ```typescript
 * columnDefs: ColDef<Order>[] = [
 *   {
 *     field: 'actions',
 *     headerName: 'Acciones',
 *     sortable: false,
 *     filterable: false,
 *     width: 150,
 *     cellRenderer: ActionButtonsRendererComponent,
 *     cellRendererParams: {
 *       buttons: [
 *         {
 *           icon: 'visibility',
 *           tooltip: 'Ver',
 *           color: 'primary',
 *           onClick: (data) => this.viewOrder(data)
 *         },
 *         {
 *           icon: 'edit',
 *           tooltip: 'Editar',
 *           color: 'accent',
 *           onClick: (data) => this.editOrder(data),
 *           disabled: (data) => data.statusId === 0
 *         },
 *         {
 *           icon: 'delete',
 *           tooltip: 'Eliminar',
 *           color: 'warn',
 *           onClick: (data) => this.deleteOrder(data),
 *           hidden: (data) => data.statusId === 0
 *         }
 *       ]
 *     }
 *   }
 * ];
 * ```
 */
@Component({
  selector: 'app-action-buttons-renderer',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="flex items-center gap-1">
      <button
        *ngFor="let button of visibleButtons"
        mat-icon-button
        [color]="button.color || 'default'"
        [disabled]="isButtonDisabled(button)"
        [matTooltip]="button.tooltip || ''"
        (click)="onButtonClick(button, $event)"
        class="!w-8 !h-8">
        <mat-icon class="!text-base">{{ button.icon }}</mat-icon>
      </button>
    </div>
  `,
})
export class ActionButtonsRendererComponent implements CellRenderer<any> {
  @Input() params!: CellRendererParams<any>;

  buttons: ActionButton[] = [];
  visibleButtons: ActionButton[] = [];

  ngOnInit(): void {
    this.updateButtons();
  }

  ngOnChanges(): void {
    this.updateButtons();
  }

  private updateButtons(): void {
    this.buttons = this.params.colDef?.cellRendererParams?.buttons || [];

    // Filter out hidden buttons
    this.visibleButtons = this.buttons.filter(button => {
      if (button.hidden) {
        return !button.hidden(this.params.data);
      }
      return true;
    });
  }

  isButtonDisabled(button: ActionButton): boolean {
    if (button.disabled) {
      return button.disabled(this.params.data);
    }
    return false;
  }

  onButtonClick(button: ActionButton, event: Event): void {
    // Stop event propagation to prevent row click
    event.stopPropagation();

    if (button.onClick && !this.isButtonDisabled(button)) {
      button.onClick(this.params.data, this.params);
    }
  }
}
